// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: drafts still being written while they are out of sight: a minimised
// mail composer, a Home peek closed (or left behind on another page) with text
// in its reply box. The corner shows them as a round bubble above the assistant
// icon on every page, so a draft can wait while you copy something from
// elsewhere and come back to it. The sources register here; this module stays
// small so the corner can read it without loading mail or the peeks.

import type {I18n} from '@lingui/core';
import {makeAutoObservable, observable} from 'mobx';
import type React from 'react';

export interface DraftTrayEntry {
	key: string;
	/** Who or what it is for: the mail subject or recipient, the chat's name. */
	title: (i18n: I18n) => string;
	/** The start of what was typed. */
	preview: string;
	/** A face or logo for the list. */
	avatar: () => React.ReactNode;
	/** The source's logo on the avatar's corner (WhatsApp, Outlook, ...). */
	badge?: () => React.ReactNode;
	/** Bring the draft back where it was written. */
	open: () => void;
	/** Put it away without sending: a mail goes to Outlook's Drafts, a chat keeps its text. */
	dismiss: (i18n: I18n) => void;
}

type Provider = () => Array<DraftTrayEntry>;

class DraftTrayStore {
	private providers = observable.array<Provider>([], {deep: false});
	/** Mail composers are open (any view): the app-wide dock loads while there are. */
	mailOpen = false;

	constructor() {
		makeAutoObservable<this, 'providers'>(this, {providers: false}, {autoBind: true});
	}

	/** Adds a source of drafts; the returned function removes it again. */
	register(provider: Provider): () => void {
		this.providers.push(provider);
		return () => {
			this.providers.remove(provider);
		};
	}

	get entries(): Array<DraftTrayEntry> {
		return this.providers.flatMap((provider) => provider());
	}

	setMailOpen(open: boolean): void {
		this.mailOpen = open;
	}
}

export default new DraftTrayStore();
